import { useConnectedConnectors } from '~/features/onboarding/hooks/use-connections';

import { toCurationConnectorCode } from '../mappers/catalog-operations.mapper';
import type { ConnectorCodeWire } from '../types/catalog-operations.api.types';

/**
 * Resolves the subsidiary's connection id for a batch's connector.
 *
 * The batch header carries no `connectionId` of its own (TODO(BE): add one to
 * `jobs/{id}/header` so callers don't need this indirection) — only the short
 * connector code (`GMC`/`META`/…). This converts it to the long form
 * (`GOOGLE_MERCHANT`/`META_COMMERCE`) via the existing mapper helper and
 * matches it against the subsidiary's connected connectors, the same
 * connector-code → connectionId pattern `scheduled-enrichment-adapter.ts` uses.
 * Returns `undefined` while connectors are loading or no connector code is
 * known yet, and `null` once resolved but the subsidiary has no matching
 * connection (distinct states so callers can tell "still loading" from
 * "genuinely unresolvable").
 */
export function useBatchConnectionId(connectorCode: string | null | undefined): {
  connectionId: string | null | undefined;
  isLoading: boolean;
} {
  const { data: connectors, isLoading } = useConnectedConnectors();
  if (isLoading) return { connectionId: undefined, isLoading: true };
  // The batch header types this loosely as `string` (it just threads the wire
  // value through); `toCurationConnectorCode`'s default case passes an
  // unrecognized code through unchanged, so an unexpected value degrades to
  // "no match" rather than throwing.
  const longCode = toCurationConnectorCode((connectorCode ?? null) as ConnectorCodeWire | null);
  if (!longCode || !connectors) return { connectionId: null, isLoading: false };
  const match = connectors.find((c) => c.code === longCode);
  return { connectionId: match?.connectionId ?? null, isLoading: false };
}
